/**
 * Backend Branding Card · Legal Module · v2.7.2
 * ─────────────────────────────────────────────
 * Frontend UI for the "Backend Branding" engine at
 *   /api/admin/legal/backend-branding/*
 *
 * Lives inside Admin Console → Legal tab as the first card.
 *
 * Surface:
 *   • Color-wheel input + hex/rgba text override for each of 2 color slots
 *   • Drag-and-drop image upload (≤50 MB, PNG/JPEG/WebP) for each of 2 image slots
 *   • Live preview swatch per element (image > color > default)
 *   • Auto-save on color change (debounced) · explicit save for uploads
 *   • Clear-asset (delete) and Factory-reset (wipe all) buttons
 *
 * Backend endpoints used:
 *   GET    /admin/legal/backend-branding
 *   PUT    /admin/legal/backend-branding/colors
 *   POST   /admin/legal/backend-branding/asset/{field}   (multipart)
 *   DELETE /admin/legal/backend-branding/asset/{field}
 *   POST   /admin/legal/backend-branding/factory-reset
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  Trash2,
  Upload,
  Palette,
  Layers,
} from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp";

const SLOTS = [
  {
    key: "global_background",
    label: "Global Background",
    urlField: "global_background_image_url",
    colorField: "global_background_color",
    description:
      "Applied behind every screen (rider, driver, admin). Falls back to the static color if no image, then the system default.",
  },
  {
    key: "navigation_bar",
    label: "Navigation Bar",
    urlField: "navigation_bar_image_url",
    colorField: "navigation_bar_color",
    description:
      "Renders behind the top nav across the platform. Same fallback chain as Global Background.",
  },
];

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";


// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
function toAssetURL(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${BASE_URL}${maybeRelative}`;
}

function isValidColor(v) {
  if (!v) return true; // empty = clear
  const t = v.trim();
  if (/^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(t)) return true;
  if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+|1\.0+)\s*)?\)$/i.test(t)) return true;
  return false;
}

function hexFromColor(v) {
  if (!v) return "#000000";
  const t = v.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    return "#" + t.slice(1).split("").map((c) => c + c).join("").toLowerCase();
  }
  return "#000000";
}


// ----------------------------------------------------------------------------
// Single slot row
// ----------------------------------------------------------------------------
function BrandingSlotRow({ slot, raw, pendingColor, resolved, onColorChange, onUpload, onClear }) {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const localColor = pendingColor ?? (raw[slot.colorField] || "");
  const serverColor = raw[slot.colorField] || "";
  const dirty = localColor !== serverColor;

  const currentUrl = raw[slot.urlField] || "";
  const previewSrc = currentUrl ? toAssetURL(currentUrl) : "";
  const previewColor = resolved.value || "#0a0a0a";

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        toast.error("Only PNG, JPEG, or WebP allowed.");
        return;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 50 MB.`);
        return;
      }
      setUploadBusy(true);
      try {
        await onUpload(slot, file);
        toast.success(`${slot.label} image saved`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setUploadBusy(false);
      }
    },
    [onUpload, slot]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files?.[0];
      if (f) handleFile(f);
    },
    [handleFile]
  );

  const commitColor = useCallback(
    (value) => {
      if (!isValidColor(value)) {
        toast.error("Invalid color. Use #hex or rgb()/rgba().");
        return;
      }
      onColorChange(slot, value);
    },
    [onColorChange, slot]
  );

  return (
    <Card
      className="bg-zinc-950/60 border-zinc-800 p-4 space-y-3"
      data-testid={`bb-slot-${slot.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Layers className="h-3.5 w-3.5 text-amber-400" />
            <h4 className="font-bold text-sm" data-testid={`bb-slot-label-${slot.key}`}>
              {slot.label}
            </h4>
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">{slot.description}</p>
        </div>
        <div
          className="w-14 h-14 rounded border border-zinc-700 overflow-hidden flex-shrink-0 relative"
          data-testid={`bb-preview-${slot.key}`}
          style={!previewSrc ? { backgroundColor: previewColor } : undefined}
          aria-label="Resolved preview"
        >
          {previewSrc && (
            <img
              src={previewSrc}
              alt={`${slot.label} preview`}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`rounded border-2 border-dashed cursor-pointer transition-colors p-4 text-center ${
          dragOver
            ? "border-amber-400 bg-amber-400/10"
            : "border-zinc-700 hover:border-amber-400/40 bg-zinc-900/40"
        }`}
        data-testid={`bb-dropzone-${slot.key}`}
      >
        {uploadBusy ? (
          <div className="flex items-center justify-center gap-2 text-amber-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Uploading…</span>
          </div>
        ) : (
          <div className="text-xs text-zinc-400">
            <Upload className="h-4 w-4 mx-auto mb-1 text-amber-400" />
            <div className="font-semibold text-zinc-300">
              {currentUrl ? "Replace image" : "Drop or click to upload"}
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">PNG · JPEG · WebP · ≤ 50 MB</div>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = "";
          }}
          data-testid={`bb-file-input-${slot.key}`}
        />
      </div>

      {/* Color row */}
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
        <input
          type="color"
          value={hexFromColor(localColor)}
          onChange={(e) => commitColor(e.target.value)}
          className="h-9 w-12 rounded cursor-pointer bg-zinc-900 border border-zinc-700"
          aria-label={`${slot.label} color`}
          data-testid={`bb-color-wheel-${slot.key}`}
        />
        <Input
          value={localColor}
          onChange={(e) => onColorChange(slot, e.target.value)}
          placeholder="#0a0a0a · rgba(10,10,10,1) · empty to clear"
          className="bg-zinc-900 border-zinc-700 h-9 font-mono text-xs"
          data-testid={`bb-color-input-${slot.key}`}
        />
        <span
          className="text-[10px] uppercase tracking-widest px-1 whitespace-nowrap"
          data-testid={`bb-source-${slot.key}`}
          style={{ color: dirty ? "#fbbf24" : "#71717a" }}
        >
          {dirty
            ? "Unsaved"
            : resolved.source === "custom_image"
            ? "Image"
            : resolved.source === "custom_color"
            ? "Color"
            : "Default"}
        </span>
      </div>

      {/* Clear image button */}
      {currentUrl && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onClear(slot)}
            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 text-[11px] h-7"
            data-testid={`bb-clear-${slot.key}`}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Remove image (revert to color)
          </Button>
        </div>
      )}
    </Card>
  );
}


// ----------------------------------------------------------------------------
// Container
// ----------------------------------------------------------------------------
export default function BackendBrandingCard() {
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState({});
  const [resolved, setResolved] = useState({ elements: {}, raw: {} });
  const [resetting, setResetting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Pending color edits (slot.colorField → value). Synced from server on load.
  const [pending, setPending] = useState({});

  const applyResp = useCallback((data) => {
    const newRaw = data?.raw || data?.resolved?.raw || {};
    setRaw(newRaw);
    setResolved(data?.resolved || { elements: {}, raw: {} });
    // Reset pending edits to match what's now persisted.
    const fresh = {};
    SLOTS.forEach((s) => { fresh[s.colorField] = newRaw[s.colorField] || ""; });
    setPending(fresh);
    // Notify any mounted consumers (e.g. the Admin layout) to re-pull
    // /api/site/backend-branding so the live page reflects the change.
    try { window.dispatchEvent(new CustomEvent("backend-branding-changed")); } catch {/* noop */}
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/legal/backend-branding");
      applyResp(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Backend Branding");
    } finally {
      setLoading(false);
    }
  }, [applyResp]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onColorChangeLocal = useCallback((slot, value) => {
    // Stage the edit. Save happens on the global SAVE button (or upload).
    setPending((p) => ({ ...p, [slot.colorField]: value }));
  }, []);

  const onSaveColors = useCallback(async () => {
    // Send only color fields that differ from server state.
    const payload = {};
    let invalid = null;
    SLOTS.forEach((s) => {
      const desired = (pending[s.colorField] ?? "");
      const current = raw[s.colorField] || "";
      if (desired !== current) {
        if (!isValidColor(desired)) {
          invalid = `${s.label}: invalid color "${desired}"`;
          return;
        }
        payload[s.colorField] = desired;
      }
    });
    if (invalid) { toast.error(invalid); return; }
    if (Object.keys(payload).length === 0) {
      toast.info("No color changes to save.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/legal/backend-branding/colors", payload);
      applyResp({ resolved: data?.resolved });
      toast.success("Colors saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [pending, raw, applyResp]);

  const onUpload = useCallback(
    async (slot, file) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post(
        `/admin/legal/backend-branding/asset/${slot.key}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      applyResp({ resolved: data?.resolved });
    },
    [applyResp]
  );

  const onClear = useCallback(
    async (slot) => {
      try {
        const { data } = await adminApi.delete(
          `/admin/legal/backend-branding/asset/${slot.key}`
        );
        applyResp({ resolved: data?.resolved });
        toast.success(`${slot.label} image cleared`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Clear failed");
      }
    },
    [applyResp]
  );

  const onFactoryReset = useCallback(async () => {
    if (!window.confirm(
      "Reset Backend Branding to defaults?\n\n• Both images will be removed.\n• Both colors will be cleared.\n• This cannot be undone."
    )) return;
    setResetting(true);
    try {
      const { data } = await adminApi.post("/admin/legal/backend-branding/factory-reset");
      applyResp({ resolved: data?.resolved });
      toast.success("Backend Branding reset to defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  }, [applyResp]);

  const slotRows = useMemo(
    () =>
      SLOTS.map((slot) => (
        <BrandingSlotRow
          key={slot.key}
          slot={slot}
          raw={raw}
          pendingColor={pending[slot.colorField]}
          resolved={resolved.elements?.[slot.key] || { kind: "color", value: "", source: "system_default" }}
          onColorChange={onColorChangeLocal}
          onUpload={onUpload}
          onClear={onClear}
        />
      )),
    [raw, pending, resolved, onColorChangeLocal, onUpload, onClear]
  );

  const anyDirty = useMemo(
    () => SLOTS.some((s) => (pending[s.colorField] ?? "") !== (raw[s.colorField] || "")),
    [pending, raw]
  );

  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-5" data-testid="backend-branding-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <Palette className="h-4 w-4 text-amber-400" />
            BACKEND BRANDING
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Platform-wide background &amp; navigation styling · uploads ≤ 50 MB · color autosaves on change ·
            image wins over color, color wins over system default.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={onSaveColors}
            disabled={saving || loading || !anyDirty}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bb-save"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                <Upload className="h-3.5 w-3.5 mr-1" />
                {anyDirty ? "SAVE CHANGES" : "SAVED"}
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onFactoryReset}
            disabled={resetting || loading}
            className="bg-zinc-950 border-zinc-700 hover:border-red-400 hover:text-red-400 text-[11px]"
            data-testid="bb-factory-reset"
          >
            {resetting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Factory Reset
              </>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div
          className="flex items-center justify-center py-10 text-zinc-500"
          data-testid="bb-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading Backend Branding…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="bb-slots">
          {slotRows}
        </div>
      )}
    </Card>
  );
}
